import React, { useState, useContext, useMemo } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { getTenantDetails } from 'utils/utils';
import ExpensesFiltersV2 from './components/ExpensesFiltersV2/ExpensesFiltersV2';
import { UserDetailsContext } from 'context/userDetailsContext';
import { CreateExpenseV2 } from './components/CreateExpenseV2/CreateExpenseV2';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../config/queryClient';
import { getPageLimit, pickExtractedDate, getFilePathForPagination } from './helpers/expensesHelpers';
import { handlePageNumberInPagination } from 'common/pagination';
import {
  caseBody as caseBodyHelper,
  typeBody as typeBodyHelper,
  teamMemberBody as teamMemberBodyHelper,
  statusBody as statusBodyHelper,
  totalAmountBody as totalAmountBodyHelper,
  dateBody as dateBodyHelper,
  actionBody as actionBodyHelper,
} from './helpers/expensesDataTableHelpers';
import { useExpenseListV2Query } from './hooks/queries/useExpenseListV2Query';
import { useDeleteExpenseQuery } from './hooks/mutations/useDeleteExpenseQuery';

const ExpensesV2 = (props) => {
  const {
    isLeadSpecific,
    isFromCaseSpecificModule,
    getTimeEntrySummarty,
    isFromGlobalBilling,
    caseView,
    caseDetails,
    isActiveTab = true,
  } = props;
  const userContext = useContext(UserDetailsContext);
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  // State management
  const [modal, setModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [filters, setFilters] = useState({
    searchKey: '',
    selectedDate: '',
    selectedStatus: undefined,
    selectedPractice: null,
  });
  const [pageLimit, setPageLimit] = useState(getPageLimit(isLeadSpecific, isFromCaseSpecificModule, userContext) ?? 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null); // No default sort - user can choose
  const [sortOrder, setSortOrder] = useState(null); // No default sort order

  const [tenantId] = getTenantDetails();

  // Map DataTable field names to API sort_by field names
  // API supports: expense_date, total_amount, case_name, team_member_name, expense_status, expense_type
  const getCurrentSortField = useMemo(() => {
    if (!sortField) return ''; // No sort field selected
    const fieldMap = {
      case_id: 'case_name', // API uses case_name for sorting
      expense_type: 'expense_type',
      team_member_name: 'team_member_name',
      expense_status: 'expense_status',
      total_amount: 'total_amount',
      expense_date: 'expense_date',
    };
    return fieldMap[sortField] || '';
  }, [sortField]);

  const getCurrentSortOrder = useMemo(() => {
    if (sortOrder === null || sortOrder === undefined) return ''; // No sort order selected
    return sortOrder === 1 ? 'asc' : 'desc';
  }, [sortOrder]);

  const queryFilters = useMemo(() => {
    const baseFilters = {
      case_id: caseView ? caseDetails?.case_id : '',
      expense_status: filters.selectedStatus || '',
      practice_area_list: filters.selectedPractice || [],
      expense_date_lower: '',
      expense_date_upper: '',
      sort_by: getCurrentSortField,
      sort_order: getCurrentSortOrder,
      search_key: filters.searchKey || '',
      ...pickExtractedDate(filters.selectedDate, userContext?.userDetails?.timezone),
    };
    return baseFilters;
  }, [caseView, caseDetails, filters, userContext, getCurrentSortField, getCurrentSortOrder]);

  // Use React Query hook for RDS-based pagination
  const {
    expenses,
    totalRecords,
    currentPage: apiCurrentPage,
    isLoading,
    refetch,
  } = useExpenseListV2Query({
    limit: pageLimit,
    page: currentPage,
    expense_status: queryFilters.expense_status,
    practice_area_list: queryFilters.practice_area_list,
    case_id: queryFilters.case_id,
    expense_date_lower: queryFilters.expense_date_lower,
    expense_date_upper: queryFilters.expense_date_upper,
    sort_by: queryFilters.sort_by,
    sort_order: queryFilters.sort_order,
    search_key: queryFilters.search_key,
    enabled: isActiveTab, // Only fetch when tab is active
  });

  // Delete expense mutation
  const deleteExpense = useDeleteExpenseQuery({
    onSuccess: () => {
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.EXPENSE_DELETE_SUCCESS);
      refetch();
      typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
    },
    onError: () => {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.EXPENSE_DELETE_FAILURE);
    },
  });

  // Table body renderers using helpers
  const caseBody = (rowData) => caseBodyHelper({ rowData });
  const typeBody = (rowData) => typeBodyHelper({ rowData });
  const teamMemberBody = (rowData) => teamMemberBodyHelper({ rowData, tenantId });
  const statusBody = (rowData) => statusBodyHelper({ rowData });
  const totalAmountBody = (rowData) => totalAmountBodyHelper({ rowData });
  const dateBody = (rowData) => dateBodyHelper({ rowData, timezone: userContext?.userDetails?.timezone });
  const actionBody = (rowData) =>
    actionBodyHelper({
      rowData,
      onEdit: (rowData) => {
        setSelectedExpense({ ...rowData });
        setModal(true);
      },
      onDelete: (rowData) => {
        setSelectedExpense({ ...rowData });
        setConfirmDelete(true);
      },
    });

  // Handlers
  const onCreateSuccess = () => {
    setModal(false);
    // Invalidate and refetch expenses
    queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });
    refetch();
    typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
  };

  const onDeleteExpense = () => {
    if (!selectedExpense?.sk) return;

    deleteExpense.mutate({
      expense_sk: selectedExpense.sk,
      is_lead: props.isLeadSpecific ? props.isLeadSpecific : selectedExpense?.is_lead,
    });
  };

  // Handle filter changes from ExpensesFiltersV2
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    // Optionally reset sorting when filters change
    // setSortField('');
    // setSortOrder(null);
  };

  // Pagination handlers for RDS-based pagination
  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
    // Update pagination preference in user context
    let path = getFilePathForPagination(isLeadSpecific, isFromCaseSpecificModule);
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, newPageSize);
  };

  // Sorting handler - matches pattern from PersonalInjuryMedicalReportRds
  const onSort = (event) => {
    const { sortField: field, sortOrder: order } = event;

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  return (
    <div>
      <ConfirmDialog
        className="confirm-delete-popup"
        visible={confirmDelete}
        onHide={() => setConfirmDelete(false)}
        header="Confirm Delete"
        rejectClassName="p-button-secondary outline"
        message={' Do you want to delete the expense data?'}
        icon="pi pi-exclamation-triangle"
        accept={() => {
          onDeleteExpense();
          setConfirmDelete(false);
        }}
        reject={() => setConfirmDelete(false)}
      />
      {modal && (
        <CreateExpenseV2
          modal={modal}
          setModal={setModal}
          onCreateSuccess={onCreateSuccess}
          selectedExpense={selectedExpense}
          caseView={caseView}
          caseDetails={caseDetails}
          isLeadSpecific={isLeadSpecific}
        />
      )}
      <div className="container-fluid mx-0 p-0">
        <div className="">
          <div className="col-12">
            <div className="d-flex  align-items-center mb-3">
              <Button
                label="Add Expense"
                icon="pi pi-plus"
                className="p-button p-button-primary ms-auto"
                onClick={() => {
                  setSelectedExpense();
                  setModal(true);
                }}
              ></Button>
            </div>
          </div>
        </div>
        <ExpensesFiltersV2
          onFiltersChange={handleFiltersChange}
          filterType="expenses"
          isFromCaseSpecificModule={isFromCaseSpecificModule}
          isLeadSpecific={isLeadSpecific}
        />

        <div className="col-12 p-0 datatable-responsive">
          <ResizableDataTable
            value={expenses || []}
            className="p-datatable-responsive"
            emptyMessage={isLoading ? 'Loading...' : 'No records found'}
            paginator={false}
            rows={pageLimit}
            storageKey="expenses-list"
            sortMode="server"
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort={false}
            columns={[
              {
                field: 'case_id',
                header: 'Case/Lead',
                body: caseBody,
                clickable: true,
                clickableKey: 'case_id',
                bodyClassName: 'ellipsis-text',
                hidden: !isFromGlobalBilling,
                sortable: isFromGlobalBilling,
              },
              {
                field: 'expense_type',
                header: 'Type',
                body: typeBody,
                bodyClassName: 'ellipsis-text',
                sortable: true,
              },
              {
                field: 'team_member_name',
                header: 'Team Member',
                body: teamMemberBody,
                bodyClassName: 'ellipsis-text',
                sortable: true,
              },
              {
                field: 'expense_status',
                header: 'Status',
                body: statusBody,
                bodyClassName: 'ellipsis-text',
                sortable: true,
              },
              {
                field: 'total_amount',
                header: 'Total Amount',
                body: totalAmountBody,
                bodyClassName: 'ellipsis-text',
                sortable: true,
              },
              {
                field: 'expense_date',
                header: 'Date',
                body: dateBody,
                bodyClassName: 'ellipsis-text',
                sortable: true,
              },
              { field: 'action', bodyClassName: 'action-column', body: actionBody, sortable: false },
            ]}
          />
          {/* External pagination using EllipsisPagination */}

          <EllipsisPagination
            currentPage={apiCurrentPage || currentPage}
            totalRecords={totalRecords}
            pageSize={pageLimit}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            showPageSizeDropdown={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ExpensesV2;
